import { m } from '@/lib/paraglide/messages'

export type FeePayer = 'customer' | 'company'

interface CodFreightFieldsProps {
    // undefined = "không chọn" — COD is then hidden on the detail page and box
    // label. true = thu, false = không thu.
    collectCod: boolean | undefined
    onCollectCodChange: (value: boolean | undefined) => void
    // undefined = "không chọn" — freight hidden on detail page + box label.
    feePayer: FeePayer | undefined
    onFeePayerChange: (value: FeePayer | undefined) => void
    // id must be unique per mounted instance — the create screen renders the
    // desktop and mobile forms together (responsive show/hide).
    idPrefix: string
    // codDisabled khoá phần "Thu tiền (COD)" (dùng khi đơn được trừ vào số dư
    // trả trước — khách đã trả nên không thu COD). Freight vẫn bật.
    codDisabled?: boolean
    customerFreightDisabled?: boolean
    customerFreightDisabledReason?: string
}

interface CheckOptionProps {
    id: string
    checked: boolean
    onToggle: () => void
    label: string
    disabled?: boolean
    disabledReason?: string
}

function CheckOption({
    id,
    checked,
    onToggle,
    label,
    disabled = false,
    disabledReason,
}: CheckOptionProps) {
    const reasonID = disabledReason ? `${id}-reason` : undefined
    return (
        <div className="space-y-1">
            <label
                htmlFor={id}
                className={
                    'flex items-center gap-2 text-sm ' +
                    (disabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer')
                }
            >
                <input
                    type="checkbox"
                    id={id}
                    checked={checked}
                    onChange={onToggle}
                    disabled={disabled}
                    aria-describedby={reasonID}
                    className="size-4 accent-primary"
                />
                {label}
            </label>
            {disabledReason && (
                <p
                    id={reasonID}
                    className="text-xs leading-snug text-muted-foreground"
                >
                    {disabledReason}
                </p>
            )}
        </div>
    )
}

// CodFreightFields renders "Thu tiền (COD)" and "Thu cước vận chuyển" as pairs of
// mutually-exclusive checkboxes. Fully controlled so it drops into both the
// create forms (react-hook-form) and the edit form (useState). Both are
// tri-state: checking one option unchecks the other; clicking the already-checked
// option clears the selection (undefined = "không chọn" → hidden on label/detail).
export function CodFreightFields({
    collectCod,
    onCollectCodChange,
    feePayer,
    onFeePayerChange,
    idPrefix,
    codDisabled = false,
    customerFreightDisabled = false,
    customerFreightDisabledReason,
}: CodFreightFieldsProps) {
    const toggleCod = (value: boolean) =>
        onCollectCodChange(collectCod === value ? undefined : value)
    const toggleFreight = (value: FeePayer) =>
        onFeePayerChange(feePayer === value ? undefined : value)

    return (
        <div className="space-y-4 rounded-lg border bg-card p-3">
            <div className="space-y-2">
                <div className="text-sm font-medium leading-none">
                    {m.order_form_collect_cod_label()}
                </div>
                <div className="grid grid-cols-2 gap-2 pl-4">
                    <CheckOption
                        id={`${idPrefix}-cod-yes`}
                        checked={collectCod === true}
                        onToggle={() => toggleCod(true)}
                        label={m.common_yes()}
                        disabled={codDisabled}
                    />
                    <CheckOption
                        id={`${idPrefix}-cod-no`}
                        checked={collectCod === false}
                        onToggle={() => toggleCod(false)}
                        label={m.common_no()}
                        disabled={codDisabled}
                    />
                </div>
            </div>
            <div className="space-y-2">
                <div className="text-sm font-medium leading-none">
                    {m.order_form_collect_freight_label()}
                </div>
                <div className="grid grid-cols-2 gap-2 pl-4">
                    <CheckOption
                        id={`${idPrefix}-freight-company`}
                        checked={feePayer === 'company'}
                        onToggle={() => toggleFreight('company')}
                        label={m.order_freight_status_company()}
                    />
                    <CheckOption
                        id={`${idPrefix}-freight-customer`}
                        checked={feePayer === 'customer'}
                        onToggle={() => toggleFreight('customer')}
                        label={m.order_freight_status_customer()}
                        disabled={customerFreightDisabled}
                        disabledReason={customerFreightDisabledReason}
                    />
                </div>
            </div>
        </div>
    )
}
